import { useState, useEffect } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";

const navLinks = [
    { href: "#about", label: "About" },
    { href: "#skills", label: "Expertise" },
    { href: "#projects", label: "Work" },
    { href: "#education", label: "Education" },
    { href: "#experience", label: "Journey" },
    { href: "#contact", label: "Contact" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  
  // Smart Scroll Logic (Hide on scroll down, Show on scroll up)
  const { scrollY } = useScroll();
  
  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious();
    
    // Set Scrolled State
    if (latest > 50) {
        setIsScrolled(true);
    } else {
        setIsScrolled(false);
    }

    // Set Visibility (Hide on scroll down, show on scroll up)
    if (latest > previous && latest > 150) {
        setVisible(false);
    } else {
        setVisible(true);
    }
  });

  const handleNavClick = (href) => {
    setIsOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isOpen) {
        document.body.style.overflow = 'hidden';
    } else {
        document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  return (
    <>
        <motion.nav
            variants={{
                visible: { y: 0 },
                hidden: { y: "-100%" },
            }}
            animate={visible ? "visible" : "hidden"}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                isScrolled ? "py-4" : "py-6"
            }`}
        >
            <div className="container px-4 md:px-8 mx-auto">
                <div 
                    className={`relative flex items-center justify-between px-6 py-3 rounded-full transition-all duration-300 ${
                        isScrolled 
                        ? "bg-white/70 backdrop-blur-xl border border-white/20 shadow-lg shadow-black/5" 
                        : "bg-transparent"
                    }`}
                >
                    {/* --- LOGO --- */}
                    <a
                        href="#"
                        className="relative z-50 text-xl font-bold tracking-tighter text-slate-900 mix-blend-difference"
                        onClick={(e) => {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                    >
                        Anant Mishra<span className="text-blue-600">.</span>
                    </a>

                    {/* --- DESKTOP NAV --- */}
                    <div className="hidden md:flex items-center gap-2">
                        {navLinks.map((link) => (
                        <a
                            key={link.href}
                            href={link.href}
                            onClick={(e) => {
                                e.preventDefault();
                                handleNavClick(link.href);
                            }}
                            className="group relative px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                        >
                            {link.label}
                            {/* Hover Underline Animation */}
                            <span className="absolute left-1/2 bottom-1 w-0 h-px bg-blue-600 transition-all duration-300 group-hover:w-1/2 group-hover:-translate-x-1/2" />
                        </a>
                        ))}
                        
                        {/* CTA Button */}
                        <a 
                            href="#contact"
                            className="ml-4 px-6 py-2.5 bg-slate-900 text-white rounded-full text-sm font-bold hover:bg-blue-600 transition-colors duration-300 flex items-center gap-2 group"
                        >
                            Let's Talk
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </a>
                    </div>

                    {/* --- MOBILE MENU TOGGLE --- */}
                    <button
                        className="md:hidden relative z-50 p-2 text-slate-900"
                        onClick={() => setIsOpen(!isOpen)}
                        aria-label="Toggle menu"
                    >
                        <div className="w-6 h-6 flex flex-col justify-center gap-1.5">
                            <motion.span 
                                animate={isOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
                                className="w-full h-0.5 bg-slate-900 block transition-transform" 
                            />
                            <motion.span 
                                animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
                                className="w-full h-0.5 bg-slate-900 block transition-opacity" 
                            />
                            <motion.span 
                                animate={isOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
                                className="w-full h-0.5 bg-slate-900 block transition-transform" 
                            />
                        </div>
                    </button>
                </div>
            </div>
        </motion.nav>

        {/* --- FULL SCREEN MOBILE MENU --- */}
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="fixed inset-0 z-40 bg-white/95 backdrop-blur-3xl md:hidden flex items-center justify-center"
                >
                    <div className="flex flex-col items-center gap-8">
                        {navLinks.map((link, index) => (
                            <motion.a
                                key={link.href}
                                href={link.href}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 20 }}
                                transition={{ delay: 0.1 + index * 0.1 }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleNavClick(link.href);
                                }}
                                className="text-4xl font-bold text-slate-900 hover:text-blue-600 transition-colors tracking-tight"
                            >
                                {link.label}
                            </motion.a>
                        ))}
                        
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 20 }}
                            transition={{ delay: 0.5 }}
                            className="mt-8"
                        >
                            <a 
                                href="#contact"
                                onClick={() => setIsOpen(false)}
                                className="px-8 py-4 bg-blue-600 text-white rounded-full text-lg font-bold shadow-lg shadow-blue-600/30"
                            >
                                Get in Touch
                            </a>
                        </motion.div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    </>
  );
};

export default Navbar;